import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import { SocketContext } from "../../../contexts/SocketContext";
import SupplierContext from "../../../contexts/SupplierContext";
import UserContext from "../../../contexts/UserContext";
import IUnit_Of_Measurement_Set from "../../../models/IUnit_Of_Measurement_Set";
import INewUnit_Of_Measurement_Set from "../../../models/INewUnit_Of_Measurement_Set";
import OwButton from "../../shared/OwButton/OwButton";
import OwTitle from "../../shared/OwTitle/OwTitle";
import TitleTagEnum from "../../../models/TitleTagEnum";
import OwLabel from "../../shared/OwLabel/OwLabel";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import UIService from "../../../services/UIService";
import ICatalogOnProcessUomSetDTO from "../../../models/DTO/ICatalogOnProcessUomSetDTO";
import ICatalogProcessUomSetDTO from "../../../models/DTO/ICatalogProcessUomSetDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import OwCheckbox from "../../shared/OwCheckbox/OwCheckbox";
import { OwDeleteEditActions } from "../../shared/OwDeleteEditActions/OwDeleteEditActions";
import { OwAcceptCancelActions } from "../../shared/OwAcceptCancelActions/OwAcceptCancelActions";

type Props = {
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
    cancelFn: (refresh?: boolean) => void;
    unitOfMeasureSet?: IUnit_Of_Measurement_Set;
    unitOfMeasureSetRelatedUnits?: IUnit_Of_Measurement_Set[];
};

interface IUoMSetRelatedUnitUI extends IUnit_Of_Measurement_Set {
    editMode?: boolean;
    originalValues?: IUnit_Of_Measurement_Set;
}

interface INewUoMSetRelatedUnitUI extends INewUnit_Of_Measurement_Set {
    editMode?: boolean;
    originalValues?: INewUnit_Of_Measurement_Set;
}

export const UnitOfMeasureSetsSidebar = (props: Props) => {
    const socketModel = useContext(SocketContext);
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);

    const [uomSet, setUomSet] = useState<INewUnit_Of_Measurement_Set | IUnit_Of_Measurement_Set>(
        props.unitOfMeasureSet || ({} as INewUnit_Of_Measurement_Set)
    );
    const [newRelatedUnit, setNewRelatedUnit] = useState<INewUnit_Of_Measurement_Set | undefined>();
    const [uomSetRelatedUnits, setUomSetRelatedUnits] = useState<(IUoMSetRelatedUnitUI | INewUoMSetRelatedUnitUI)[]>(
        props.unitOfMeasureSetRelatedUnits || []
    );

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setUomSet({ ...uomSet, [name]: value });
    };

    const handleNewRelatedUnit = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNewRelatedUnit({ ...newRelatedUnit, [e.target.name]: e.target.value } as INewUnit_Of_Measurement_Set);
    };

    const addRelatedUnit = () => {
        if (newRelatedUnit) {
            setUomSetRelatedUnits([...uomSetRelatedUnits, newRelatedUnit]);
            setNewRelatedUnit(undefined);
        }
    };

    const toggleEditMode = (uomSetRelatedUnit: IUoMSetRelatedUnitUI | INewUoMSetRelatedUnitUI, index: number, revert: boolean) => {
        if (uomSetRelatedUnit.editMode) {
            if (revert) {
                uomSetRelatedUnit.unit_name = uomSetRelatedUnit.originalValues?.unit_name || "";
                uomSetRelatedUnit.unit_abbr = uomSetRelatedUnit.originalValues?.unit_abbr || "";
                uomSetRelatedUnit.conversion_factor = uomSetRelatedUnit.originalValues?.conversion_factor || 0;
            }
        } else {
            uomSetRelatedUnit.originalValues = { ...uomSetRelatedUnit };
        }
        uomSetRelatedUnit.editMode = !uomSetRelatedUnit.editMode;
        const newUomSetRelatedUnits = [...uomSetRelatedUnits];
        newUomSetRelatedUnits[index] = uomSetRelatedUnit;
        setUomSetRelatedUnits(newUomSetRelatedUnits);
    };

    const toggleDeleteFlag = (uomSetRelatedUnit: IUoMSetRelatedUnitUI | INewUoMSetRelatedUnitUI, index: number) => {
        if ("id" in uomSetRelatedUnit) {
            uomSetRelatedUnit.deleteFlag = !uomSetRelatedUnit.deleteFlag;
            const newUomSetRelatedUnits = [...uomSetRelatedUnits];
            newUomSetRelatedUnits[index] = uomSetRelatedUnit;
            setUomSetRelatedUnits(newUomSetRelatedUnits);
        } else {
            const newUomSetRelatedUnits = [...uomSetRelatedUnits];
            newUomSetRelatedUnits.splice(index, 1);
            setUomSetRelatedUnits(newUomSetRelatedUnits);
        }
    };

    const handleUomRelatedUnitChange = (
        e: ChangeEvent<HTMLInputElement>,
        uomRelatedUnit: IUoMSetRelatedUnitUI | INewUoMSetRelatedUnitUI,
        index: number
    ) => {
        const { name, value } = e.target;
        // @ts-expect-error
        uomRelatedUnit[name] = value;
        const newUomSetRelatedUnits = [...uomSetRelatedUnits];
        newUomSetRelatedUnits[index] = uomRelatedUnit;
        setUomSetRelatedUnits(newUomSetRelatedUnits);
    };

    const saveSet = () => {
        const relatedUnits = uomSetRelatedUnits.map((uomSetRelatedUnit) => {
            const relatedUnit: INewUnit_Of_Measurement_Set | IUnit_Of_Measurement_Set = {
                supplier_id: supplierModel.id,
                unit_name: uomSetRelatedUnit.unit_name,
                unit_abbr: uomSetRelatedUnit.unit_abbr,
                conversion_factor: uomSetRelatedUnit.conversion_factor,
                is_hidden: uomSetRelatedUnit.is_hidden,
            };

            if ("id" in uomSetRelatedUnit && uomSetRelatedUnit.id) {
                // @ts-expect-error
                relatedUnit.id = uomSetRelatedUnit.id;
                // @ts-expect-error
                relatedUnit.guid = uomSetRelatedUnit.guid;
                // @ts-expect-error
                relatedUnit.deleteFlag = uomSetRelatedUnit.deleteFlag;
            }

            return relatedUnit;
        });

        const payload: ICatalogProcessUomSetDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            uomSet,
            relatedUnits,
        };

        socketModel?.emit(EndpointsEnum.CatalogsProcessUomSet, payload);
    };

    const onProcessUomSet = (data: ICatalogOnProcessUomSetDTO) => {
        props.cancelFn(true);
    };

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.CatalogsOnProcessUomSet, fn: onProcessUomSet }];

    useEffect(() => {
        for (const socket of sockets) {
            socketModel?.on(socket.endpoint, socket.fn);
        }

        return () => {
            for (const socket of sockets) {
                socketModel?.removeListener(socket.endpoint, socket.fn);
            }
        };
    }, []);

    let tableClass = "table";

    if (!UIService.isMainViewLight()) {
        tableClass += " table-dark";
    }

    return (
        <div className={"UnitOfMeasureSetsSidebar p-2"}>
            <div className={"text-end"}>
                <OwButton
                    centerIconClass={"fas fa-times"}
                    className={"btn-danger"}
                    fn={() => {
                        props.cancelFn();
                    }}
                />
            </div>
            <div>
                <OwTitle title={"uom_set"} leftIconName={"fad fa-cart-flatbed-boxes"} tag={TitleTagEnum.H3} />
            </div>

            <div className="mt-3">
                <div>
                    <OwLabel title={"base_unit"} isBold={true} />
                </div>
                <div>
                    <div>
                        <OwLabel title={"name"} />
                    </div>
                    <div>
                        <OwFnInput fn={handleChange} name={"unit_name"} value={uomSet.unit_name || ""} />
                    </div>
                </div>

                <div className={"mt-2"}>
                    <div>
                        <OwLabel title={"abbr"} />
                    </div>
                    <div>
                        <OwFnInput fn={handleChange} name={"unit_abbr"} value={uomSet.unit_abbr || ""} />
                    </div>
                </div>

                <div className={"mt-2"}>
                    <div>
                        <OwCheckbox
                            name={"is_hidden"}
                            fn={handleChange}
                            label={"is_hidden"}
                            truthyVal={true}
                            falsyVal={false}
                            checked={uomSet.is_hidden}
                        />
                    </div>
                    <div className={"text-muted text-small"}>
                        <OwLabel title={"uom_set_hidden_message"} leftIconClass={"fad fa-info-circle"} />
                    </div>
                </div>
            </div>

            <div className={"mt-3"}>
                <OwLabel title={"related_units"} isBold={true} className={"mb-2"} />
                <div className={"d-flex align-items-end mb-2"}>
                    <div className={"me-2"}>
                        <div>
                            <OwLabel title={"name"} />
                        </div>
                        <OwFnInput fn={handleNewRelatedUnit} name={"unit_name"} value={newRelatedUnit?.unit_name || ""} />
                    </div>
                    <div className={"me-2"}>
                        <div>
                            <OwLabel title={"abbr"} />
                        </div>
                        <OwFnInput fn={handleNewRelatedUnit} name={"unit_abbr"} value={newRelatedUnit?.unit_abbr || ""} />
                    </div>
                    <div className={"me-2"}>
                        <div>
                            <OwLabel title={"conversion_factor"} />
                        </div>
                        <OwFnInput
                            fn={handleNewRelatedUnit}
                            name={"conversion_factor"}
                            type={"number"}
                            value={newRelatedUnit?.conversion_factor || 0}
                        />
                    </div>
                    <div>
                        <OwButton title={"add"} leftIconClass={"fas fa-plus"} fn={addRelatedUnit} />
                    </div>
                </div>
                <table className={tableClass}>
                    <tr>
                        <th></th>
                        <th>
                            <OwLabel title={"name"} />
                        </th>
                        <th>
                            <OwLabel title={"abbr"} />
                        </th>
                        <th>
                            <OwLabel title={"conversion_factor"} />
                        </th>
                    </tr>
                    {uomSetRelatedUnits.map((uomSetRelatedUnit, index) => {
                        let deleteFlag = false;

                        if ("id" in uomSetRelatedUnit) {
                            deleteFlag = uomSetRelatedUnit.deleteFlag || false;
                        }

                        return (
                            <tr key={index}>
                                <th>
                                    <OwDeleteEditActions
                                        isEditMode={!!uomSetRelatedUnit.editMode}
                                        onTrashClickFn={() => {
                                            toggleDeleteFlag(uomSetRelatedUnit, index);
                                        }}
                                        onEditClickFn={() => {
                                            toggleEditMode(uomSetRelatedUnit, index, false);
                                        }}
                                    />
                                    <OwAcceptCancelActions
                                        isEditMode={!uomSetRelatedUnit.editMode}
                                        onAcceptClickFn={() => {
                                            toggleEditMode(uomSetRelatedUnit, index, false);
                                        }}
                                        onCancelClickFn={() => {
                                            toggleEditMode(uomSetRelatedUnit, index, true);
                                        }}
                                    />
                                </th>
                                <td>
                                    {uomSetRelatedUnit.editMode ? (
                                        <OwFnInput
                                            fn={(e: ChangeEvent<HTMLInputElement>) => {
                                                handleUomRelatedUnitChange(e, uomSetRelatedUnit, index);
                                            }}
                                            name={"unit_name"}
                                            value={uomSetRelatedUnit.unit_name || ""}
                                        />
                                    ) : (
                                        <span className={deleteFlag ? "text-strikethrough" : ""}>{uomSetRelatedUnit.unit_name}</span>
                                    )}
                                </td>
                                <td>
                                    {uomSetRelatedUnit.editMode ? (
                                        <OwFnInput
                                            fn={(e: ChangeEvent<HTMLInputElement>) => {
                                                handleUomRelatedUnitChange(e, uomSetRelatedUnit, index);
                                            }}
                                            name={"unit_abbr"}
                                            value={uomSetRelatedUnit.unit_abbr || ""}
                                        />
                                    ) : (
                                        <span className={deleteFlag ? "text-strikethrough" : ""}>{uomSetRelatedUnit.unit_abbr}</span>
                                    )}
                                </td>
                                <td>
                                    {uomSetRelatedUnit.editMode ? (
                                        <OwFnInput
                                            fn={(e: ChangeEvent<HTMLInputElement>) => {
                                                handleUomRelatedUnitChange(e, uomSetRelatedUnit, index);
                                            }}
                                            name={"conversion_factor"}
                                            type={"number"}
                                            value={uomSetRelatedUnit.conversion_factor || 0}
                                        />
                                    ) : (
                                        <span className={deleteFlag ? "text-strikethrough" : ""}>
                                            {uomSetRelatedUnit.conversion_factor}
                                        </span>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </table>
            </div>

            <div className={"mt-3"}>
                <OwButton title={"save"} leftIconClass={"fas fa-save"} className={"btn-primary"} fn={saveSet} />
            </div>
        </div>
    );
};
